import { append, any, propEq, clone } from 'ramda';
import {
  getAttribute,
  getAssociation,
  UNKNOWN_ASSOCIATION,
  UNKNOWN_ATTRIBUTE,
  findCollection,
} from '../../../Model/avenyModelUtils';
import {
  IUIPage,
  IUIUrval,
  ITypeTreeConditionObject,
  UIUrvalConditionObjectType,
  IStringConditionObject,
  INumericConditionObject,
  TEDUrvalType,
  IBooleanConditionObject,
  IChecklistConditionObject,
  IExtendedCondition,
  IExtendedConditionObject,
  IUrvalCondition,
  IValue,
  UIConditionType,
} from '../../../Types/UrvalTypes';
import {
  IClientAttributeModel,
  IClientAssociationModel,
} from '../../../Types/ClientModelTypes';
import shortid from 'shortid';
import {
  getTypeTreeConditionObject,
  getDefaultConditionObject,
} from '../Components/UrvalFieldPicker/Utils/UrvalConditionUtils';
import { AvenyCollectionMap } from '../../../Model/ModelContext/ModelContext';
import {
  EK_KONTO_URVAL_COLLECTION_NAME,
  URVAL_CHECKLIST_NO_VALUE_DISPLAY_LABEL,
  URVAL_CHECKLIST_NO_VALUE_VALUE,
} from '../Consts/UrvalConsts';
import mergeSort from '../../../Utils/mergeSort';
import Logger, { namedError } from '../../../Utils/Logger';
import { ISumData } from '../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import {
  rangesFromUIUrval,
  getEnumCode,
} from '../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/SideMenu/Components/UrvalOchSummering/Components/Urval/SimpleUrval/Utils/SimpleUrvalUtils';
import { kodplaner_ekkodplaner } from '../../KodplanManager/Queries/_QueryTypes/kodplaner';
import {
  TEDSelectionConditionType,
  TEDSelectionOperator,
} from '../../../globalQueryTypes';
import { AnalysLevelEnum } from '../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Const/EkonomiskAnalysConsts';
import {
  IUrvalUsedForDrilling,
  createConditionObjectWithChecklistUrval,
  createConditionObjectWithKoddelUrval,
} from '../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Utils/detaljanalysUtils';

export interface IUrvalInfo {
  urval: IUIUrval;
  newSums: ISumData[];
}

export const getAttributeSafe = (
  collectionName: string,
  attributeName: string,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel => {
  const attr = getAttribute(collectionName, '', attributeName, collectionMap);

  if (attr != null) {
    return attr;
  } else {
    Logger.error(
      `Could not get ModelData for attribute ${attributeName} from collection ${collectionName}`
    );
    return UNKNOWN_ATTRIBUTE;
  }
};

export const getAssociationSafe = (
  collectionName: string,
  associationName: string,
  collectionMap: AvenyCollectionMap
): IClientAssociationModel => {
  const assoc = getAssociation(
    collectionName,
    '',
    associationName,
    collectionMap
  );

  if (assoc != null) {
    return assoc;
  } else {
    Logger.error(
      `Could not get ModelData for association ${associationName} from collection ${collectionName}`
    );
    return UNKNOWN_ASSOCIATION;
  }
};

interface ICheckListValueAndLabel {
  displayLabel: string;
  value: string;
}

export const getChecklistValuesAndLabels = (
  attribute: IClientAttributeModel
): ICheckListValueAndLabel[] => {
  const list = attribute.EnumValues
    ? Array.from(attribute.EnumValues).map(
        ([value, displayLabel]): ICheckListValueAndLabel => ({
          value,
          displayLabel,
        })
      )
    : [];

  if (attribute.Required) {
    return list;
  } else {
    return append(
      {
        displayLabel: URVAL_CHECKLIST_NO_VALUE_DISPLAY_LABEL,
        value: URVAL_CHECKLIST_NO_VALUE_VALUE,
      },
      list
    );
  }
};

interface ICheckListValueAndLabelWithCode extends ICheckListValueAndLabel {
  code: string | undefined;
}

export const getChecklistValuesAndLabelsWithCode = (
  attribute: IClientAttributeModel
): ICheckListValueAndLabelWithCode[] => {
  const list: ICheckListValueAndLabelWithCode[] = attribute.EnumValues
    ? Array.from(attribute.EnumValues).map(codeValuePair => {
        const displayLabel = codeValuePair[1];

        return {
          displayLabel,
          value: codeValuePair[0],
          code: getEnumCode(displayLabel),
        };
      })
    : [];

  return append(
    {
      displayLabel: URVAL_CHECKLIST_NO_VALUE_DISPLAY_LABEL,
      value: URVAL_CHECKLIST_NO_VALUE_VALUE,
      code: undefined,
    },
    list
  );
};

export const getEmptyPage = (): IUIPage => ({
  id: shortid.generate(),
  conditionObjects: [],
});

// Urval med en tom sida
export const getEmptyUrval = (): IUIUrval => ({
  pages: [getEmptyPage()],
});

// Helt tomt urval
export const emptyUrvalNoPages = { pages: [] };

export const getNewUrval = (
  collectionMap: AvenyCollectionMap,
  collectionName: string,
  componentName?: string
): IUIUrval => ({
  pages: [getNewPage(collectionMap, collectionName, componentName)],
});

export const getNewPage = (
  collectionMap: AvenyCollectionMap,
  collectionName: string,
  componentName?: string
): IUIPage => {
  const typeTreeConditionObject = getTypeTreeConditionObject(
    collectionName,
    componentName != null ? componentName : '',
    collectionMap
  );

  if (typeTreeConditionObject == null) {
    return getEmptyPage();
  } else {
    return {
      id: shortid.generate(),
      conditionObjects: [typeTreeConditionObject],
    };
  }
};

export const conditionExistsInPage = (page: IUIPage, id: string) =>
  any(propEq('id', id), page.conditionObjects);

export const getConditionIndex = (page: IUIPage, id: string) =>
  page.conditionObjects.findIndex(propEq('id', id));

/**
 * Returnerar en lista med alla kontodelar som finns med i den aktiva kodplanen
 */
export const getKontodelList = (
  collectionMap: AvenyCollectionMap
): IClientAttributeModel[] => {
  const kontoCollection = findCollection(collectionMap, 'EKKonto');
  const kontodelList: IClientAttributeModel[] = [];

  if (kontoCollection == null) {
    Logger.warn(
      namedError(
        'urvalUtils-KontoCollectionIsNull',
        `kontoCollection is null or undefined`
      )
    );
  } else {
    const kontoAttributes = kontoCollection.Attributes;

    Object.keys(kontoAttributes).forEach(key => {
      const kontodel = kontoAttributes[key];
      if (kontodel == null) {
        Logger.warn(
          namedError(
            'urvalUtils-KontodelIsNull',
            `kontodel ${key} is null or undefined`
          )
        );
      } else {
        if (kontodel.Condition === 'Normal') {
          kontodelList.push(kontodel);
        }
      }
    });
  }

  return kontodelList;
};
export interface ITypeConditionInfo {
  index: number;
  object: ITypeTreeConditionObject;
}

/**
 * Searches for a type condition object on the first level of condition objects, returns both the index and condition object itself
 */
export const getTypeConditionObject = (
  conditionObjects: UIUrvalConditionObjectType[]
): ITypeConditionInfo | undefined => {
  const typeConditionObjectIndex = conditionObjects.findIndex(
    conditionObject =>
      conditionObject.type === 'TypeTree' &&
      (conditionObject.modelPath == null ||
        conditionObject.modelPath.length === 0)
  );

  let typeConditionObject: UIUrvalConditionObjectType | undefined =
    conditionObjects[typeConditionObjectIndex];

  if (
    typeConditionObject == null ||
    (typeConditionObject && typeConditionObject.type !== 'TypeTree')
  ) {
    return undefined;
  }

  return {
    index: typeConditionObjectIndex,
    object: typeConditionObject,
  };
};

export const getAttributeNames = (
  conditionObjects: UIUrvalConditionObjectType[]
): string[] => {
  return conditionObjects
    .filter(o => !o.hideInSimpleUrval)
    .map(o => {
      return getAttributeName(o);
    });
};

export const getAttributeName = (
  conditionObject: UIUrvalConditionObjectType
): string => {
  return conditionObject.type === 'Extended'
    ? conditionObject.association.AssociationName
    : conditionObject.attribute.AttributeName;
};

export const urvalFromAttributeNames = (
  collectionMap: AvenyCollectionMap,
  attributeNames: string[],
  analysLevel?: AnalysLevelEnum,
  urvalConditionsIUrvalUsedForDrilling?: IUrvalUsedForDrilling[]
): IUIUrval => {
  const collection = collectionMap.get(
    EK_KONTO_URVAL_COLLECTION_NAME.toLowerCase()
  );
  let conditionObjects: UIUrvalConditionObjectType[] = [];
  if (collection) {
    attributeNames.forEach(name => {
      const attribute = collection.Attributes[name];

      if (
        !urvalConditionsIUrvalUsedForDrilling ||
        urvalConditionsIUrvalUsedForDrilling.length === 0
      ) {
        const conditionObject = getDefaultConditionObject(attribute);
        conditionObjects.push(conditionObject);
        return;
      }

      urvalConditionsIUrvalUsedForDrilling.forEach(drilledUrval => {
        if (
          !drilledUrval.attributeDisplayName ||
          !drilledUrval.conditionValue ||
          !attribute
        ) {
          const conditionObject = getDefaultConditionObject(attribute);
          conditionObjects.push(conditionObject);
          return;
        }

        const isMatchingAttribute =
          attribute.DisplayLabel === drilledUrval.attributeDisplayName;
        if (!isMatchingAttribute) {
          return;
        }

        // Check if this should be a checklist urval (has enum values and valid analysis level)
        if (attribute.EnumValues && analysLevel !== undefined) {
          const conditionObject = createConditionObjectWithChecklistUrval(
            attribute,
            drilledUrval.conditionValue.toString()
          );
          conditionObjects.push(conditionObject);
          return;
        }

        // Default to koddel urval
        const conditionObject = createConditionObjectWithKoddelUrval(
          attribute,
          drilledUrval.conditionValue.toString(),
          drilledUrval.missingValue
        );
        conditionObjects.push(conditionObject);
      });
    });
  }

  const updatedUrval = getEmptyUrval();
  updatedUrval.pages[0].conditionObjects = conditionObjects;
  return updatedUrval;
};

export const sortOnAttributeList = (
  conditionObjects: UIUrvalConditionObjectType[],
  attributeList: string[]
) => {
  return mergeSort(conditionObjects, (a, b) => {
    const indexA = attributeList.indexOf(getAttributeName(a));
    const indexB = attributeList.indexOf(getAttributeName(b));
    const compareA = indexA === -1 ? attributeList.length : indexA;
    const compareB = indexB === -1 ? attributeList.length : indexB;
    return compareA - compareB;
  });
};

export const uniqueFromOtherUrval = (
  a: IUIUrval,
  b: IUIUrval
): UIUrvalConditionObjectType[] => {
  return a.pages[0].conditionObjects.filter(
    x =>
      !b.pages[0].conditionObjects.find(
        c => getAttributeName(c) === getAttributeName(x)
      )
  );
};

export const getSumsInfo = (
  sums: ISumData[],
  collectionMap: AvenyCollectionMap,
  oldSums: ISumData[],
  urval?: IUIUrval
): IUrvalInfo => {
  const sumsUrval = urvalFromAttributeNames(
    collectionMap,
    sums.map(sum => sum.attributename).filter(name => name !== null) as string[]
  );
  let newUrval = sumsUrval;
  if (urval) {
    newUrval = clone(urval);
    const newSumUrval = uniqueFromOtherUrval(sumsUrval, urval);
    newUrval.pages[0].conditionObjects.push(...newSumUrval);
    const attributeNames = sums.map(sum => sum.attributename);
    newUrval.pages[0].conditionObjects = sortOnAttributeList(
      newUrval.pages[0].conditionObjects,
      attributeNames.filter(a => a !== null) as string[]
    );

    const ranges = rangesFromUIUrval(newUrval);
    const allUrvalToRemove = oldSums.filter(
      sum => !sums.find(s => s.attributename === sum.attributename)
    );
    const urvalToRemove = allUrvalToRemove.filter(x =>
      Object.keys(ranges).some(
        key => key === x.attributename && ranges[key] === ''
      )
    );
    removeUrvalBySums(newUrval, urvalToRemove);
  }
  return {
    urval: newUrval,
    newSums: sums,
  };
};

export const removeUrvalBySums = (
  urval: IUIUrval,
  toRemove: ISumData[]
): IUIUrval => {
  urval.pages[0].conditionObjects = urval.pages[0].conditionObjects.filter(x =>
    toRemove.every(u => u.attributename !== getAttributeName(x))
  );
  return urval;
};

export const urvalFromKodplan = (
  kodplan: kodplaner_ekkodplaner,
  collectionMap: AvenyCollectionMap
) => {
  const attributeNames = kodplan.koddelar
    ? kodplan.koddelar.map(koddel => `KONTODEL_${koddel.ordnings_nr || 0}`)
    : [];

  const urval = urvalFromAttributeNames(collectionMap, attributeNames);

  return urval;
};

const stringNumericHasValue = (
  cond: IStringConditionObject | INumericConditionObject
): boolean =>
  cond.condition.some(c =>
    c.values.some(
      cc =>
        (cc.value != null && cc.value !== '') ||
        (cc.toValue != null && cc.toValue !== '')
    )
  );

export const conditionHasValue = (
  condition: UIUrvalConditionObjectType
): boolean => {
  switch (condition.type) {
    case 'Boolean':
      return condition.condition.no != null || condition.condition.yes != null;
    case 'Checklist':
      return condition.condition.length > 0;
    case 'Extended':
      return condition.condition.some(cond =>
        cond.in.some(page => page.conditionObjects.some(conditionHasValue))
      );
    case 'Numeric':
      return condition.condition.length > 0 && stringNumericHasValue(condition);
    case 'String':
      return condition.condition.length > 0 && stringNumericHasValue(condition);
    case 'TypeTree':
      return condition.condition.length > 0;
    default:
      return true;
  }
};

export const pageHasValue = (page: IUIPage) =>
  page.conditionObjects.some(conditionObject =>
    conditionHasValue(conditionObject)
  );

export const urvalHasValue = (urval: IUIUrval | null): boolean =>
  urval != null && urval.pages.some(page => pageHasValue(page));

/**
 * Creates an Urval from a list of gql-ids where the result is a list of objects with matching ids
 */
export const createIdsUrval = (ids: string[]): TEDUrvalType => {
  return [
    [
      {
        namn: 'id',
        typ: TEDSelectionConditionType.Value,
        valueCondition: {
          op: TEDSelectionOperator.equals,
          value: ids.map(id => ({ value: id })),
        },
      },
    ],
  ];
};

/**
 * Tar in en IUIPage och ersätter ids med en tom sträng eller fältnamn
 * @param page IUIPage
 * @returns IUIPage
 */
export const removeIDsFromIUIPage = (page: IUIPage) => {
  const newConditions: UIUrvalConditionObjectType[] = [];

  page.conditionObjects.forEach(condition => {
    let tempCondition: UIUrvalConditionObjectType = { ...condition };

    if (condition.type === 'String') {
      const conditionObjects: IUrvalCondition[] = [];
      condition.condition.forEach(selectionCondition => {
        const tempValues: IValue[] = [];
        selectionCondition.values.forEach(value => {
          const tempValue: IValue = { ...value, id: '' };

          tempValues.push(tempValue);
        });
        const tempCond: IUrvalCondition = {
          ...selectionCondition,
          id: '',
          values: tempValues,
        };

        conditionObjects.push(tempCond);
      });
      const stringCondition: IStringConditionObject = {
        ...condition,
        id: condition.attribute.AttributeName,
        condition: conditionObjects,
      };
      tempCondition = { ...stringCondition };
    }
    if (condition.type === 'Numeric') {
      const conditionObjects: IUrvalCondition[] = [];
      condition.condition.forEach(selectionCondition => {
        const tempValues: IValue[] = [];
        selectionCondition.values.forEach(value => {
          const tempValue: IValue = { ...value, id: '' };

          tempValues.push(tempValue);
        });
        const tempCond: IUrvalCondition = {
          ...selectionCondition,
          id: '',
          values: tempValues,
        };

        conditionObjects.push(tempCond);
      });
      const numericCondition: INumericConditionObject = {
        ...condition,
        id: condition.attribute.AttributeName,
        condition: conditionObjects,
      };
      tempCondition = { ...numericCondition };
    }
    if (condition.type === 'TypeTree') {
      const typeTreeCondition: ITypeTreeConditionObject = {
        ...condition,
        id: condition.attribute.AttributeName,
      };
      tempCondition = { ...typeTreeCondition };
    }
    if (condition.type === 'Extended') {
      const conditionObjects: IExtendedCondition[] = [];

      condition.condition.forEach(extendedCondition => {
        const extendedPages = extendedCondition.in.map(page =>
          removeIDsFromIUIPage(page)
        );

        const tempCond: IExtendedCondition = {
          ...extendedCondition,
          id: '',
          in: extendedPages,
        };

        conditionObjects.push(tempCond);
      });
      const extendedCondition: IExtendedConditionObject = {
        ...condition,
        id: condition.association.AssociationName,
        condition: conditionObjects,
      };
      tempCondition = { ...extendedCondition };
    }
    if (condition.type === 'Checklist') {
      const checklistCondition: IChecklistConditionObject = {
        ...condition,
        id: condition.attribute.AttributeName,
      };
      tempCondition = { ...checklistCondition };
    }
    if (condition.type === 'Boolean') {
      const booleanCondition: IBooleanConditionObject = {
        ...condition,
        id: condition.attribute.AttributeName,
      };
      tempCondition = { ...booleanCondition };
    }

    newConditions.push(tempCondition);
  });
  const tempIUIPage: IUIPage = {
    ...page,
    id: '',
    conditionObjects: newConditions,
  };

  return tempIUIPage;
};

/**
 * Ersätter ids med en tom sträng eller fältnamn i ett IUIUrval så att man kan jämnföra värdena med ett annat IUIUrval.
 * @param selection - IUIUrval | null
 * @returns IUIUrval | null
 */
export const removeIDsFromIUIUrval = (selection: IUIUrval | null) => {
  if (!selection) {
    return selection;
  } else {
    const newIUIPages: IUIPage[] = [];

    selection.pages.forEach(page => {
      const tempIUIPage = removeIDsFromIUIPage(page);

      newIUIPages.push(tempIUIPage);
    });

    return { ...selection, pages: newIUIPages };
  }
};

/**
 * Type guard that checks if a condition object has an attribute property.
 *
 * This function narrows down UIUrvalConditionObjectType to exclude IExtendedConditionObject,
 * since IExtendedConditionObject has an 'association' property instead of 'attribute'.
 *
 * @param obj - The condition object to check
 * @returns True if the object has an attribute property, false otherwise
 *
 * @example
 * ```typescript
 * conditionObjects.forEach(conditionObject => {
 *   if (hasAttribute(conditionObject)) {
 *     // TypeScript now knows conditionObject has an 'attribute' property
 *     const displayName = conditionObject.attribute.DisplayLabel;
 *   } else {
 *     // TypeScript knows this must be IExtendedConditionObject
 *     const associationName = conditionObject.association.Name;
 *   }
 * });
 * ```
 */
export const isConditionObjectWithAttribute = (
  obj: UIUrvalConditionObjectType
): obj is Exclude<UIUrvalConditionObjectType, IExtendedConditionObject> => {
  return 'attribute' in obj;
};

/**
 * Type guard that checks if a condition is an array of IUrvalCondition objects.
 *
 * This function validates that the condition is:
 * 1. An array
 * 2. Contains at least one element
 * 3. The first element is an object with an 'op' property (characteristic of IUrvalCondition)
 *
 * This is useful for distinguishing IUrvalCondition[] from other types in UIConditionType
 * such as string[], boolean, or IUrvalBooleanCondition.
 *
 * @param condition - The condition to check, can be any UIConditionType
 * @returns True if condition is IUrvalCondition[], false otherwise
 *
 * @example
 * ```typescript
 * if (hasAttribute(conditionObject) && hasConditionValues(conditionObject.condition)) {
 *   // TypeScript knows conditionObject.condition is IUrvalCondition[]
 *   const firstValue = conditionObject.condition[0].values[0].value;
 * }
 * ```
 */
export const containsValidConditions = (
  condition: UIConditionType
): condition is IUrvalCondition[] => {
  return (
    Array.isArray(condition) &&
    condition.length > 0 &&
    typeof condition[0] === 'object' &&
    condition[0] !== null &&
    'op' in condition[0]
  );
};
